import {
  deleteTeamSettings,
  downloadTeamCSV,
  getTeamSettings,
  sendTeamInviteEmail,
  sendTeamOnboardEmail,
  sendTeamDatapeopleAnywhereEmail,
  updateTeamSettings,
  updateJobNavPermission,
  getAdministrators,
  getDemographicSurveyAdmins,
  getDemographicSurveyUsers,
} from '@/api/settings';
import { handleCSVDownload } from '@/helpers/fileDownload';
import { handleKnownSettingsApiErrors } from '@/stores/settings/errorHandling';
import { defineStore, storeToRefs } from 'pinia';
import { ref, computed } from 'vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useAppLoading } from '@/stores/app/loading';
import { useAppPagination } from '@/stores/app/pagination';
import { useModals } from '@/stores/modals/modals';
import { useSettingsTeamInviteForm } from '@/stores/settings/teamInviteForm';
import { useSettingsIntegrations } from '@/stores/settings/integrations';
import { manageTeamTooltips } from '@/helpers/constants/manageTeamTooltips';
import { useFilters } from '../filters/filters';
import { useAppSnackbar } from '../app/snackbar';

export const useSettingsTeam = defineStore('settingsTeam', () => {
  // other stores
  const form = useSettingsTeamInviteForm();
  const { setLoading } = useAppLoading();
  const authData = useAppAuthData();
  const pagination = useAppPagination();
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();
  const { setModal } = useModals();
  const { allowIntegratedUserInvite } = storeToRefs(useSettingsIntegrations());

  // state
  const teamList = ref([]);
  const selectedMembers = ref([]);
  const inviteLoading = ref([]);
  const invited = ref([]);
  const inviteErrors = ref({});
  const administrators = ref([]);
  const demographicSurveyAdmins = ref([]);
  const demographicSurveyUsers = ref([]);

  const state = {
    sortAttributes: ref([]),
    selectedSortOption: ref('name'),
    searchTerm: ref(''),
    totals: ref({}),
  };

  // getters
  const selectedCount = computed(() => selectedMembers.value.length);
  const selectedMemberIds = computed(() => selectedMembers.value.map((member) => member.id));
  const selectedSortOptionDesc = computed(() => state.selectedSortOption.value?.startsWith('-'));
  const displayableTeamList = computed(
    () => teamList.value.map((user) => ({
      ...user,
      isSelectable: user.status === 'inactive',
      persona: user.persona || 'Not Specified',
    })),
  );
  const teamParams = computed(() => ({
    searchTerm: state.searchTerm.value,
    sort: state.selectedSortOption.value,
    page: pagination.page,
  }));

  const headers = computed(() => {
    /**
     *  Controls content shown on teams table.
     *  Omit jobs & roles columns for non-integrated projects
     * @param name
     */
    const isSortable = (name) => state.sortAttributes.value.includes(name);
    const allHeaders = [
      {
        title: 'Name',
        align: 'start',
        sortable: isSortable('name'),
        value: 'name',
        width: authData.integrated ? '155px' : '235px',
      },
      {
        title: 'Job Role',
        align: 'start',
        sortable: isSortable('roles'),
        value: 'roles',
        width: '150px',
        tooltip: manageTeamTooltips.jobRole,
      },
      {
        title: 'Job Posts',
        align: 'start',
        sortable: isSortable('jobCount'),
        value: 'jobCount',
        width: '110px',
      },
      {
        title: 'Persona',
        align: 'start',
        sortable: isSortable('persona'),
        value: 'persona',
        width: '220px',
        tooltip: manageTeamTooltips.persona,
      },
      {
        title: 'JobNav Recruiter Permission',
        align: 'start',
        sortable: isSortable('isAdmin'),
        value: 'isAdmin',
        width: authData.integrated ? '245px' : '195px',
        tooltip: manageTeamTooltips.datapeoplePermissions,
      },
      {
        title: 'JobNav Permission',
        align: 'start',
        sortable: false,
        value: 'jpRole',
        width: '195px',
        tooltip: manageTeamTooltips.jobNavPermissions,
      },
      {
        title: 'JobNav Action',
        align: 'start',
        sortable: false,
        value: 'status',
        width: '200px',
        tooltip: manageTeamTooltips.datapeopleActions,
      },
    ];
    const excluded = authData.integrated ? [] : ['jobCount', 'roles'];
    if (!authData.authData.jobProfileEnabled) excluded.push('jpRole');
    return excluded.length
      ? allHeaders.filter((item) => !excluded.includes(item.value))
      : allHeaders;
  });

  const headersWithMenu = computed(() => [
    ...headers.value,
    {
      title: '', align: 'start', sortable: false, value: 'action', width: '54px',
    },
  ]);

  // mutations
  const setTeamList = (team) => {
    teamList.value = team;
  };

  const setInviteLoading = ({ id, loading }) => {
    if (loading) {
      inviteLoading.value = [...inviteLoading.value, id];
      if (!invited.value.includes(id)) {
        invited.value = [...invited.value, id];
      }
    } else {
      inviteLoading.value = inviteLoading.value.filter((loadingId) => loadingId !== id);
    }
  };

  const setInviteErrors = ({ id, error }) => {
    inviteErrors.value = {
      ...inviteErrors.value,
      [id]: error,
    };
  };

  const setSelectedMembers = (event) => {
    selectedMembers.value = event || [];
  };

  const setTeamsView = (viewOpts) => {
    Object.keys(viewOpts).forEach((key) => {
      state[key].value = viewOpts[key];
    });
  };

  const setDemographicSurveyUsers = (users) => {
    demographicSurveyUsers.value = users;
  };

  // actions
  const loadTeam = async ({
    firstLoad = false,
    router,
    routeQuery,
    page,
    selectedSortOption,
    selectedFilters,
    searchTerm,
  }) => {
    if (firstLoad) {
      setLoading({ listLoading: true, filtersLoading: true });
      pagination.setPageNumber(page);
      setTeamsView({ searchTerm, selectedSortOption });
    } else {
      setLoading({ filtersLoading: true, listLoading: true });
      setSelectedMembers();
    }

    try {
      const {
        teamList: newTeamList,
        filters,
        textFilter,
        pagination: newPagination,
        sorting,
        totals,
      } = await getTeamSettings({
        params: routeQuery ? { ...routeQuery, ...teamParams.value } : teamParams.value,
        selectedFilters,
      });

      useFilters().updateFilters({ filters });

      setTeamList(newTeamList);
      pagination.updatePagination(newPagination);
      setTeamsView({
        searchTerm: textFilter.value || '',
        selectedSortOption: sorting.value,
        sortAttributes: sorting.attributes,
        totals,
      });

      // update URL with shareable filter link
      if (router) {
        const newQuery = { ...routeQuery, ...selectedFilters };

        if (state.searchTerm.value) {
          newQuery.q = state.searchTerm.value;
        }
        if (state.selectedSortOption.value) {
          newQuery.sort = state.selectedSortOption.value;
        }
        if (pagination.page) {
          newQuery.page = pagination.page;
        }

        router.push({
          query: newQuery,
        }).catch(() => true); // ignore duplicate navigation error due to global filter selection
      }
    } catch (error) {
      const knownError = handleKnownSettingsApiErrors({ context: 'Team settings', error });

      if (knownError) return;

      addErrorSnackbar({ text: `Team settings - ${error.message}` });
      throw error;
    } finally {
      setLoading({ filtersLoading: false, listLoading: false, pageLoading: false });
    }
  };

  const loadAdministrators = async () => {
    try {
      setLoading({ listLoading: true });

      const admins = await getAdministrators();
      administrators.value = admins;
    } catch (error) {
      const knownError = handleKnownSettingsApiErrors({ context: 'JobNav administrators', error });

      if (knownError) return;

      addErrorSnackbar({ text: `Unable to load administrators - ${error.message}` });
      throw error;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const loadDemographicSurveyAdmins = async () => {
    try {
      setLoading({ listLoading: true });
      const admins = await getDemographicSurveyAdmins();
      demographicSurveyAdmins.value = admins;
    } catch (error) {
      const knownError = handleKnownSettingsApiErrors({ context: 'Demographic survey administrators', error });

      if (knownError) return;

      addErrorSnackbar({ text: `Unable to load demographic survey report administrators - ${error.message}` });

      throw error;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const loadDemographicSurveyUsers = async () => {
    try {
      setLoading({ listLoading: true });
      const users = await getDemographicSurveyUsers();
      demographicSurveyUsers.value = users;
    } catch (error) {
      const knownError = handleKnownSettingsApiErrors({ context: 'Demographic survey administrators', error });

      if (knownError) return;

      addErrorSnackbar({ text: `Unable to demographic survey report users - ${error.message}` });

      throw error;
    } finally {
      setLoading({ listLoading: false });
    }
  };

  const downloadTeam = async (title, mergedTempFilters) => {
    try {
      const commaSeparatedString = await downloadTeamCSV({
        params: teamParams.value,
        selectedFilters: mergedTempFilters,
      });

      handleCSVDownload({ commaSeparatedString, autoClick: true, title });
    } catch (error) {
      addErrorSnackbar({ text: `Team Download Error: ${error.message}` });
      throw error;
    }
  };

  const updateTeamsView = ({
    router,
    routeQuery,
    page = false,
    searchTerm,
    selectedSortOption,
  }) => {
    if (page) {
      pagination.setPageNumber(page);
    }

    let previousSearch;
    if (typeof searchTerm === 'undefined') {
      previousSearch = state.searchTerm.value;
    }

    let selected;
    if (state.selectedSortOption.value.includes(selectedSortOption)) {
      selected = state.selectedSortOption.value.startsWith('-')
        ? selectedSortOption
        : `-${selectedSortOption}`;
    } else {
      selected = selectedSortOption || state.selectedSortOption.value;
    }

    setTeamsView({
      searchTerm: previousSearch || searchTerm,
      selectedSortOption: selected,
    });

    loadTeam({ router, routeQuery });
  };

  const submit = async ({
    action, id, routeQuery,
  }) => {
    setLoading({ pageLoading: true });
    try {
      const success = await updateTeamSettings({ action, id });
      if (success) await loadTeam({ firstLoad: false, routeQuery });
    } catch (error) {
      const knownError = handleKnownSettingsApiErrors({ context: 'Unable to update user role', error });

      if (knownError) return;

      addErrorSnackbar({ text: `Unable to update user role - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const submitJobNav = async ({ id, role, routeQuery }) => {
    setLoading({ pageLoading: true });
    try {
      const success = await updateJobNavPermission({ id, role });
      if (success) await loadTeam({ firstLoad: false, routeQuery });
    } catch (error) {
      const knownError = handleKnownSettingsApiErrors({ context: 'Unable to update JobNav role', error });

      if (knownError) return;

      addErrorSnackbar({ text: `Unable to update JobNav role - ${error.message}` });
      throw error;
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const sendDatapeopleAnywhereInvite = async (id) => {
    try {
      setInviteLoading({ id, loading: true });
      await sendTeamDatapeopleAnywhereEmail(id);
    } catch (error) {
      setInviteErrors({ id, error: error.message || 'JobNav Anywhere Invite email could not be sent' });

      const knownError = handleKnownSettingsApiErrors({ context: 'Invite email could not be sent', error });
      if (knownError) return;

      throw error;
    } finally {
      setInviteLoading({ id, loading: false });
    }
  };

  const sendOnboardingInvite = async (id) => {
    try {
      setInviteLoading({ id, loading: true });
      await sendTeamOnboardEmail([id]);
    } catch (error) {
      setInviteErrors({ id, error: error.message || 'Invite email could not be sent' });

      const knownError = handleKnownSettingsApiErrors({ context: 'Invite email could not be sent', error });
      if (knownError) return;

      throw error;
    } finally {
      setInviteLoading({ id, loading: false });
    }
  };

  const handleValidationErrors = (response) => {
    form.setBackendErrors(response.errors);
  };

  const sendOnboardingEmails = async () => {
    setLoading({ modalLoading: true });
    try {
      if (authData.integrated && selectedMemberIds.value.length > 0) {
        selectedMemberIds.value.forEach((id) => {
          sendOnboardingInvite(id);
        });
      }
      if (!authData.integrated || allowIntegratedUserInvite.value) {
        const response = await sendTeamInviteEmail(form.values.email);
        if (response.success) {
          addSuccessSnackbar({
            text: 'Email has been sent',
          });
          setModal({ field: 'settings', value: false });
        }
      }
    } catch (error) {
      const { response } = error;
      if (response && response.code === 'validation_failed') {
        handleValidationErrors(response);
      } else {
        const knownError = handleKnownSettingsApiErrors({ context: 'Invite email could not be sent', error });

        if (knownError) return;

        // catch-all error handling for generic server errors
        addErrorSnackbar({
          text: `Invite email could not be sent - ${error.message}`,
        });
        setModal({ field: 'settings', value: false });
      }
      throw error;
    } finally {
      setLoading({ modalLoading: false, pageLoading: false });
    }
  };

  const deleteUser = async ({ id, name }) => {
    try {
      const { success } = await deleteTeamSettings({ id });
      if (success) {
        const updated = teamList.value.filter((item) => item.id !== id);
        setTeamList(updated);
        addSuccessSnackbar({ text: `${name} has been removed` });
      }
    } catch (error) {
      const knownError = handleKnownSettingsApiErrors({ context: 'Unable to delete user', error });
      if (knownError) return;

      addErrorSnackbar({ text: `Unable to delete user - ${error.message}` });
      throw error;
    }
  };

  return {
    // state
    ...state,
    inviteLoading,
    selectedMembers,
    invited,
    inviteErrors,
    administrators,
    demographicSurveyAdmins,
    demographicSurveyUsers,

    // getters
    headers,
    headersWithMenu,
    displayableTeamList,
    selectedSortOptionDesc,
    selectedCount,

    // actions
    deleteUser,
    loadTeam,
    sendOnboardingEmails,
    updateTeamsView,
    downloadTeam,
    setSelectedMembers,
    setTeamList,
    setDemographicSurveyUsers,
    submit,
    submitJobNav,
    sendOnboardingInvite,
    sendDatapeopleAnywhereInvite,
    loadAdministrators,
    loadDemographicSurveyAdmins,
    loadDemographicSurveyUsers,
  };
});
